import axios, { AxiosError } from 'axios';
import { getLanguage, t, type Language } from '../i18n';
import type {
  Assignment,
  AssignmentCreate,
  AssignmentReturn,
  AuditDevice,
  AuditLog,
  BrandingSettings,
  Car,
  CarInput,
  CarSummary,
  ColumnMapping,
  Contract,
  ContractCreate,
  DashboardStats,
  Document,
  DocumentUpdate,
  DocumentUpload,
  ExpenseItem,
  ExpenseSummary,
  Driver,
  DriverInput,
  Fine,
  FuelImportPreview,
  FuelImportResult,
  FuelInput,
  FuelSummary,
  FuelTransaction,
  FineInput,
  FineSummary,
  GPSMonth,
  MaintenanceRow,
  NotificationSettings,
  GPSImportPreview,
  GPSImportResult,
  GPSSyncResult,
  GPSSyncStatus,
  GPSRide,
  PageParams,
  Paginated,
  Reservation,
  ReservationCalendar,
  ReservationInput,
  Service,
  ServiceInput,
  TireInput,
  TokenResponse,
  UpcomingService,
  User,
  UserCreate,
  UserUpdate,
  Vignette,
  VignetteCoverage,
  VignetteInput,
} from '../types';

export const API_BASE_URL: string = import.meta.env.VITE_API_URL || '/api';

// Authentication uses the httpOnly cookie set by /auth/login
const http = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

let unauthorizedHandler: (() => void) | null = null;

export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

http.interceptors.request.use((config) => {
  config.headers['X-Device-Id'] = clientFingerprint();
  // Server messages (errors, reports) come back in the user's language
  config.headers['Accept-Language'] = getLanguage();
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/auth/login')) {
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  },
);

let cachedFingerprint: string | null = null;

/**
 * Stable browser/device fingerprint for the audit log. The server records the
 * IP address, computer name and MAC address itself; this helps tell apart
 * browsers sharing one IP (e.g. a terminal server).
 */
function clientFingerprint(): string {
  if (cachedFingerprint) return cachedFingerprint;
  const parts = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}`,
    String(screen.colorDepth),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    String(navigator.hardwareConcurrency || ''),
  ].join('|');
  let hash = 0x811c9dc5;
  for (let i = 0; i < parts.length; i++) {
    hash ^= parts.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  cachedFingerprint = `FP-${(hash >>> 0).toString(16).toUpperCase().padStart(8, '0')}`;
  return cachedFingerprint;
}

const get = <T>(url: string, params?: object) => http.get<T>(url, { params: cleanParams(params) }).then((r) => r.data);
const post = <T>(url: string, body?: unknown) => http.post<T>(url, body).then((r) => r.data);
const put = <T>(url: string, body?: unknown) => http.put<T>(url, body).then((r) => r.data);
const patch = <T>(url: string, body?: unknown) => http.patch<T>(url, body).then((r) => r.data);
const del = <T = void>(url: string) => http.delete<T>(url).then((r) => r.data);

function cleanParams(params?: object) {
  if (!params) return undefined;
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

function formData(values: Record<string, string | Blob | undefined | null>) {
  const data = new FormData();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== '') data.append(key, value);
  }
  return data;
}

export function downloadUrl(documentId: number, inline = false) {
  return `${API_BASE_URL}/documents/${documentId}/download${inline ? '?inline=true' : ''}`;
}

export const authApi = {
  login: (username: string, password: string) => post<TokenResponse>('/auth/login', { username, password }),
  logout: () => post('/auth/logout'),
  me: () => get<User>('/auth/me'),
  changePassword: (current_password: string, new_password: string) =>
    post<User>('/auth/change-password', { current_password, new_password }),
  setLanguage: (language: Language) => put<User>('/auth/language', { language }),
};

export const notificationsApi = {
  settings: () => get<NotificationSettings>('/notifications/settings'),
  save: (data: { email: string | null; kinds: string[] }) => put<NotificationSettings>('/notifications/settings', data),
  test: () => post<{ detail: string }>('/notifications/test'),
};

export const usersApi = {
  list: (params?: PageParams & { search?: string }) => get<Paginated & { users: User[] }>('/users', params),
  create: (data: UserCreate) => post<User>('/users', data),
  update: (id: number, data: UserUpdate) => patch<User>(`/users/${id}`, data),
  deactivate: (id: number) => del<User>(`/users/${id}`),
};

export type CarFilters = PageParams & { status?: string; search?: string };

export const carsApi = {
  list: (params?: CarFilters) => get<Paginated & { cars: Car[] }>('/cars', params),
  get: (id: number) => get<Car>(`/cars/${id}`),
  summary: (id: number) => get<CarSummary>(`/cars/${id}/summary`),
  create: (data: CarInput) => post<Car>('/cars', data),
  update: (id: number, data: Partial<CarInput & TireInput>) => patch<Car>(`/cars/${id}`, data),
  retire: (id: number) => del<Car>(`/cars/${id}`),
};

export type DriverFilters = PageParams & { is_active?: boolean; search?: string; license_expiring?: boolean };

export const driversApi = {
  list: (params?: DriverFilters) => get<Paginated & { drivers: Driver[] }>('/drivers', params),
  get: (id: number) => get<Driver>(`/drivers/${id}`),
  create: (data: DriverInput) => post<Driver>('/drivers', data),
  update: (id: number, data: Partial<DriverInput>) => patch<Driver>(`/drivers/${id}`, data),
  deactivate: (id: number) => del<Driver>(`/drivers/${id}`),
};

export type FineFilters = PageParams & { car_id?: number; driver_id?: number; country?: string; year?: number; paid?: boolean; search?: string };

export type FuelFilters = PageParams & {
  car_id?: number;
  date_from?: string;
  date_to?: string;
  category?: string;
  source?: string;
  search?: string;
};

export const fuelApi = {
  list: (params?: FuelFilters) =>
    get<Paginated & { transactions: FuelTransaction[]; total_net: number; total_gross: number; total_liters: number }>('/fuel', params),
  summary: (params: { date_from?: string; date_to?: string; car_id?: number }) => get<FuelSummary>('/fuel/summary', params),
  create: (data: FuelInput) => post<FuelTransaction>('/fuel', data),
  update: (id: number, data: Partial<FuelInput>) => patch<FuelTransaction>(`/fuel/${id}`, data),
  delete: (id: number) => del(`/fuel/${id}`),
  preview: (files: File[]) => http.post<FuelImportPreview>('/fuel/import/preview', gpsFormData(files, {})).then((r) => r.data),
  import: (files: File[], createMissing: boolean) =>
    http.post<FuelImportResult>('/fuel/import', gpsFormData(files, { create_missing: String(createMissing) })).then((r) => r.data),
};

export const expensesApi = {
  summary: (year?: number) => get<ExpenseSummary>('/expenses/summary', { year }),
  items: (params: { year?: number; car_id?: number }) => get<ExpenseItem[]>('/expenses/items', params),
};

export const finesApi = {
  list: (params?: FineFilters) => get<Paginated & { fines: Fine[]; total_amount: number }>('/fines', params),
  summary: (year?: number) => get<FineSummary>('/fines/summary', { year }),
  create: (data: FineInput) => post<Fine>('/fines', data),
  update: (id: number, data: Partial<FineInput>) => patch<Fine>(`/fines/${id}`, data),
  delete: (id: number) => del(`/fines/${id}`),
};

export type ReservationFilters = PageParams & { car_id?: number; upcoming?: boolean; search?: string };

export const reservationsApi = {
  list: (params?: ReservationFilters) => get<Paginated & { reservations: Reservation[] }>('/reservations', params),
  get: (id: number) => get<Reservation>(`/reservations/${id}`),
  calendar: (date_from: string, date_to: string) => get<ReservationCalendar>('/reservations/calendar', { date_from, date_to }),
  create: (data: ReservationInput) => post<Reservation>('/reservations', data),
  update: (id: number, data: Partial<ReservationInput>) => patch<Reservation>(`/reservations/${id}`, data),
  delete: (id: number) => del(`/reservations/${id}`),
  handoverInternal: (id: number) => post<Reservation>(`/reservations/${id}/handover`, { mode: 'internal' }),
  addDriver: (id: number) => post<Reservation>(`/reservations/${id}/driver`),
};

export type VignetteFilters = PageParams & { car_id?: number; country?: string; validity?: string };

export const vignettesApi = {
  list: (params?: VignetteFilters) => get<Paginated & { vignettes: Vignette[]; total_price: number }>('/vignettes', params),
  overview: () => get<VignetteCoverage[]>('/vignettes/overview'),
  create: (data: VignetteInput) => post<Vignette>('/vignettes', data),
  update: (id: number, data: Partial<VignetteInput>) => patch<Vignette>(`/vignettes/${id}`, data),
  delete: (id: number) => del(`/vignettes/${id}`),
};

export type ContractFilters = PageParams & { car_id?: number; driver_id?: number; search?: string };

export const contractsApi = {
  list: (params?: ContractFilters) => get<Paginated & { contracts: Contract[] }>('/contracts', params),
  create: (data: ContractCreate) => post<Contract>('/contracts', data),
  delete: (id: number) => del(`/contracts/${id}`),
};

export function contractDownloadUrl(contractId: number) {
  return `${API_BASE_URL}/contracts/${contractId}/download`;
}

export type AssignmentFilters = PageParams & {
  car_id?: number;
  driver_id?: number;
  date_from?: string;
  date_to?: string;
  active_only?: boolean;
};

export const assignmentsApi = {
  list: (params?: AssignmentFilters) => get<Paginated & { assignments: Assignment[] }>('/assignments', params),
  create: (data: AssignmentCreate) => post<Assignment>('/assignments', data),
  returnCar: (id: number, data: AssignmentReturn) => post<Assignment>(`/assignments/${id}/return`, data),
  update: (id: number, data: Partial<Assignment>) => patch<Assignment>(`/assignments/${id}`, data),
};

export type ServiceFilters = PageParams & {
  car_id?: number;
  service_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
};

export const servicesApi = {
  list: (params?: ServiceFilters) => get<Paginated & { services: Service[]; total_cost: number }>('/services', params),
  upcoming: (days = 30) => get<UpcomingService[]>('/services/upcoming', { days }),
  overview: () => get<MaintenanceRow[]>('/services/overview'),
  create: (data: ServiceInput) => post<Service>('/services', data),
  update: (id: number, data: Partial<ServiceInput>) => patch<Service>(`/services/${id}`, data),
  delete: (id: number) => del(`/services/${id}`),
};

export type DocumentFilters = PageParams & {
  car_id?: number;
  doc_type?: string;
  expiring_soon?: boolean;
  expired?: boolean;
  search?: string;
};

export const documentsApi = {
  list: (params?: DocumentFilters) => get<Paginated & { documents: Document[] }>('/documents', params),
  upload: (file: File, data: DocumentUpload, onProgress?: (percent: number) => void) =>
    http
      .post<Document>(
        '/documents',
        formData({
          file,
          car_id: String(data.car_id),
          title: data.title,
          doc_type: data.doc_type,
          expiry_date: data.expiry_date,
          notes: data.notes,
        }),
        {
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      )
      .then((r) => r.data),
  update: (id: number, data: DocumentUpdate) => patch<Document>(`/documents/${id}`, data),
  delete: (id: number) => del(`/documents/${id}`),
};

export type RideFilters = PageParams & {
  car_id?: number;
  driver_id?: number;
  date_from?: string;
  date_to?: string;
  month_id?: number;
};

export type MonthFilters = PageParams & { car_id?: number; year?: number; month?: number };

function gpsFormData(files: File[], extra: Record<string, string | undefined>) {
  const data = new FormData();
  files.forEach((file) => data.append('files', file));
  for (const [key, value] of Object.entries(extra)) if (value !== undefined) data.append(key, value);
  return data;
}

export const gpsApi = {
  preview: (files: File[], mapping?: ColumnMapping) =>
    http
      .post<GPSImportPreview>('/gps/import/preview', gpsFormData(files, { column_mapping: mapping ? JSON.stringify(mapping) : undefined }))
      .then((r) => r.data),
  import: (files: File[], mapping: ColumnMapping | undefined, createMissing: boolean) =>
    http
      .post<GPSImportResult>(
        '/gps/import',
        gpsFormData(files, { column_mapping: mapping ? JSON.stringify(mapping) : undefined, create_missing: String(createMissing) }),
      )
      .then((r) => r.data),
  months: (params?: MonthFilters) => get<Paginated & { months: GPSMonth[]; total_distance_km: number }>('/gps/months', params),
  month: (id: number) => get<GPSMonth>(`/gps/months/${id}`),
  deleteMonth: (id: number) => del<{ deleted: number }>(`/gps/months/${id}`),
  rides: (params?: RideFilters) =>
    get<Paginated & { rides: GPSRide[]; total_distance_km: number }>('/gps/rides', params),
  syncStatus: () => get<GPSSyncStatus>('/gps/sync'),
  sync: () => post<GPSSyncResult>('/gps/sync'),
};

export type AuditFilters = PageParams & {
  table_name?: string;
  record_id?: number;
  user_id?: number;
  action?: string;
  field_name?: string;
  device?: string;
  ip_address?: string;
  mac_address?: string;
  date_from?: string;
  date_to?: string;
};

export const auditApi = {
  list: (params?: AuditFilters) => get<Paginated & { logs: AuditLog[] }>('/audit', params),
  devices: (params?: AuditFilters) => get<AuditDevice[]>('/audit/devices', params),
  exportUrl: (params?: AuditFilters) => {
    const query = new URLSearchParams(
      Object.entries(cleanParams(params) ?? {}).map(([k, v]) => [k, String(v)]),
    ).toString();
    return `${API_BASE_URL}/audit/export.csv${query ? `?${query}` : ''}`;
  },
  record: (table: string, id: number) => get<AuditLog[]>(`/audit/record/${table}/${id}`),
  tables: () => get<{ tables: string[] }>('/audit/tables'),
  actions: () => get<{ actions: string[] }>('/audit/actions'),
};

export const settingsApi = {
  public: () => get<BrandingSettings>('/settings/public'),
  update: (data: Partial<BrandingSettings>) => patch<BrandingSettings>('/settings', data),
};

export const dashboardApi = {
  stats: () => get<DashboardStats>('/dashboard/stats'),
};

/** Human readable message from an API error. */
export function errorMessage(error: unknown, fallback = t('Nastala chyba')): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(', ') || fallback;
    if (!error.response) return t('Server nie je dostupný');
  }
  return fallback;
}
